/**
 * 게시판 모듈 도메인 모델
 *
 * Supabase DB 스키마와 1:1 대응하는 인터페이스.
 * Row 타입에서 비즈니스 로직용으로 변환하여 사용.
 */
import { t } from '@lingui/macro';

/** 게시판 내 게시글 작성자 (임베디드 JSON) */
export interface PostAuthor {
  uid: string;
  name: string;
  photoUrl?: string | null;
}

/** 게시판 scope */
export type BoardScope = 'workspace' | 'channel' | 'personal';

/** 게시판 유형 */
export type BoardType = 'general' | 'notice' | 'qna' | 'issue' | 'wiki' | 'tasks';

/** 게시판 디자인 스타일 (canonical) — 3개 + wiki 전용
 *  - notion: 데이터베이스/테이블 (이전 dooray + notion 통합)
 *  - basecamp: 카드 grid (썸네일 또는 본문 미리보기)
 *  - discourse: 포럼 (좌측 썸네일 + 내용 미리보기)
 *  - wiki: 위키 전용 (게시판 type === 'wiki' 일 때만)
 *
 *  Legacy 값('dooray', 'linear')은 dispatcher 에서 fallback 처리 — DB 마이그레이션 불필요.
 *  Board.ts/resolveDisplayStyle 와 PostListView.tsx/RENDERERS 에서 자동 매핑.
 */
export type DisplayStyle = 'discourse' | 'basecamp' | 'notion' | 'wiki';

/** Legacy DB 값 → canonical 매핑 */
const LEGACY_STYLE_FALLBACK: Record<string, DisplayStyle> = {
  dooray: 'notion',
  linear: 'basecamp',
};

/** Legacy 값을 canonical 로 정규화 (dispatcher 와 모든 resolve 입구에서 사용) */
export function normalizeDisplayStyle(value: string | null | undefined): DisplayStyle | null {
  if (!value) return null;
  if (value in LEGACY_STYLE_FALLBACK) return LEGACY_STYLE_FALLBACK[value];
  if (value === 'discourse' || value === 'basecamp' || value === 'notion' || value === 'wiki') return value;
  return null;
}

/** 게시글 작성 권한 */
export type WritePermission = 'admin-only' | 'members' | 'everyone';

/** 알림 발송 권한 — @all / @개인 누가 쓸 수 있는가 */
export type NotifyPermission = 'admin-only' | 'members' | 'everyone';

/** 댓글 작성 권한 */
export type CommentPermission = 'admin-only' | 'members' | 'everyone';

/** 최소 역할 (게스트 접근 제어) */
export type MinRole = 'member' | 'guest';

// 권한 옵션 키 (UI select 표시 순서). 라벨/설명은 아래 get*Option 함수로 i18n.
export const WRITE_PERMISSIONS: WritePermission[] = ['admin-only', 'members', 'everyone'];
export const COMMENT_PERMISSIONS: CommentPermission[] = ['admin-only', 'members', 'everyone'];
export const NOTIFY_PERMISSIONS: NotifyPermission[] = ['admin-only', 'members', 'everyone'];
export const MIN_ROLES: MinRole[] = ['member', 'guest'];

/**
 * 권한 옵션 라벨/설명 (i18n) — 렌더 중 호출해야 현재 locale 반영.
 * 정적 config 로 두면 모듈 로드 시점 locale 로 고정돼 언어 전환이 반영되지 않음.
 */
export function getWritePermissionOption(k: WritePermission): { label: string; desc: string } {
  switch (k) {
    case 'admin-only': return { label: t`Admin only`, desc: t`Only admins can write posts` };
    case 'members':    return { label: t`Members`,    desc: t`Members and above can write (default)` };
    case 'everyone':   return { label: t`Everyone`,   desc: t`Anyone including guests can write` };
  }
}
export function getCommentPermissionOption(k: CommentPermission): { label: string; desc: string } {
  switch (k) {
    case 'admin-only': return { label: t`Admin only`, desc: t`Only admins can comment` };
    case 'members':    return { label: t`Members`,    desc: t`Members and above can comment` };
    case 'everyone':   return { label: t`Everyone`,   desc: t`Anyone including guests can comment (default)` };
  }
}
export function getNotifyPermissionOption(k: NotifyPermission): { label: string; desc: string } {
  switch (k) {
    case 'admin-only': return { label: t`Admin only`, desc: t`Only admins can use` };
    case 'members':    return { label: t`Members`,    desc: t`Members and above can use` };
    case 'everyone':   return { label: t`Everyone`,   desc: t`Anyone including guests can use` };
  }
}
export function getMinRoleOption(k: MinRole): { label: string; desc: string } {
  switch (k) {
    case 'member': return { label: t`Members and above`, desc: t`Guests cannot access (default)` };
    case 'guest':  return { label: t`Public`,            desc: t`Guests can also access` };
  }
}

/** 모바일에서 자동 전환이 필요한 테이블형 스타일 */
const TABLE_STYLES: DisplayStyle[] = ['notion'];

/** 일반 게시판에서 사용자가 전환 가능한 스타일 (wiki 제외) */
export const SWITCHABLE_STYLES: DisplayStyle[] = ['notion', 'basecamp', 'discourse'];

/**
 * 반응형 디자인 스타일 결정
 * 우선순위: wiki 고정 > 사용자 선호(userOverride) > 관리자 기본값(displayStyle) > 반응형 폴백
 * 입력값은 legacy DB 값('dooray'/'linear') 도 받아 normalize.
 */
export function resolveDisplayStyle(
  displayStyle: DisplayStyle | string | null,
  isMobile: boolean,
  userOverride?: DisplayStyle | string | null,
): DisplayStyle {
  const norm = (v: DisplayStyle | string | null | undefined): DisplayStyle | null =>
    normalizeDisplayStyle(typeof v === 'string' ? v : null) ?? (v as DisplayStyle | null);

  const normDisplay = norm(displayStyle);
  const normOverride = norm(userOverride);

  // wiki는 무조건 전용 렌더러
  if (normDisplay === 'wiki') return 'wiki';

  // 사용자 오버라이드 (wiki 제외)
  const base = normOverride && normOverride !== 'wiki' ? normOverride : normDisplay;

  if (!base) return isMobile ? 'discourse' : 'notion';
  if (isMobile && TABLE_STYLES.includes(base)) return 'discourse';
  return base;
}

// ── 사용자별 뷰 선호 (localStorage) ──

const VIEW_PREF_KEY_PREFIX = 'board-view-pref-';

/** 사용자의 게시판별 뷰 선호 조회 */
export function getUserViewPreference(boardId: string): DisplayStyle | null {
  try {
    const val = localStorage.getItem(`${VIEW_PREF_KEY_PREFIX}${boardId}`);
    return val as DisplayStyle | null;
  } catch { return null; }
}

/** 사용자의 게시판별 뷰 선호 저장 */
export function setUserViewPreference(boardId: string, style: DisplayStyle): void {
  try { localStorage.setItem(`${VIEW_PREF_KEY_PREFIX}${boardId}`, style); } catch {}
}

/** 사용자의 게시판별 뷰 선호 초기화 (관리자 기본값으로 복원) */
export function clearUserViewPreference(boardId: string): void {
  try { localStorage.removeItem(`${VIEW_PREF_KEY_PREFIX}${boardId}`); } catch {}
}

// ── 게시판 권한 체크 유틸 ─────────────────────────────────────

type RoleLike = 'owner' | 'admin' | 'member' | 'guest';

/**
 * 채널 게시판에서의 유효 role.
 * scope='channel' 게시판은 채널 멤버만 접근하므로, 워크스페이스 guest 라도
 * 채널 안에서는 member 동등으로 취급한다 ("채널 멤버라면 모두 이용" 사양).
 * (admin/owner/member 는 그대로 유지. 워크스페이스/개인 게시판은 영향 없음.)
 */
export function effectiveBoardRole(board: Board, role: RoleLike): RoleLike {
  if (board.scope === 'channel' && role === 'guest') return 'member';
  return role;
}

/**
 * 게시판 열람 가능 여부
 * min_role이 'member'면 guest 차단, null/'guest'면 전체 허용
 */
export function canViewBoard(board: Board, role: RoleLike): boolean {
  role = effectiveBoardRole(board, role);
  if (role === 'owner' || role === 'admin') return true;
  if (board.min_role === 'member' && role === 'guest') return false;
  return true;
}

/**
 * 게시글 작성 가능 여부
 * write_permission: 'admin-only' → admin/owner만, 'members' → member 이상, 'everyone' → 전체
 * 게시판 owner는 항상 가능
 */
/** [내부] 보드 내에서 admin 동등 자격이 있는지 — owner / 보드별 admin_ids / 워크스페이스 board_manager_user_ids / 워크스페이스 admin·owner */
function isAdminEquivalent(
  board: Board,
  role: RoleLike,
  userId?: string,
  workspaceManagerIds?: string[],
): boolean {
  if (role === 'owner' || role === 'admin') return true;
  if (userId && board.owner_id === userId) return true;
  if (userId && board.admin_ids?.includes(userId)) return true;
  if (userId && workspaceManagerIds?.includes(userId)) return true;
  return false;
}

export function canWritePost(
  board: Board,
  role: RoleLike,
  userId?: string,
  workspaceManagerIds?: string[],
): boolean {
  role = effectiveBoardRole(board, role);
  if (isAdminEquivalent(board, role, userId, workspaceManagerIds)) return true;
  switch (board.write_permission) {
    case 'admin-only': return false;
    case 'everyone': return true;
    case 'members':
    default: return role === 'member';
  }
}

/**
 * 댓글 작성 가능 여부
 * comment_permission: 'admin-only' → admin 동등만, 'members' → member 이상, 'everyone' → 전체
 */
export function canWriteComment(
  board: Board,
  role: RoleLike,
  userId?: string,
  workspaceManagerIds?: string[],
): boolean {
  role = effectiveBoardRole(board, role);
  if (isAdminEquivalent(board, role, userId, workspaceManagerIds)) return true;
  switch (board.comment_permission) {
    case 'admin-only': return false;
    case 'everyone': return true;
    case 'members':
    default: return role === 'member';
  }
}

/**
 * 게시판 관리(수정/삭제/설정) 가능 여부
 * - 게시판 owner
 * - 게시판 admin_ids 에 포함된 사용자 (보드별 위임)
 * - 워크스페이스 owner / admin
 * - 워크스페이스 board_manager_user_ids 에 포함된 사용자 (워크스페이스 단위 위임)
 */
export function canManageBoard(
  board: Board,
  role: RoleLike,
  userId?: string,
  workspaceManagerIds?: string[],
): boolean {
  if (userId && board.owner_id === userId) return true;
  if (userId && board.admin_ids?.includes(userId)) return true;
  // 채널 게시판은 방장(owner_id)·지정 관리자(admin_ids)만 관리. 워크스페이스 admin/manager 무관.
  if (board.scope === 'channel') return false;
  if (userId && workspaceManagerIds?.includes(userId)) return true;
  return role === 'owner' || role === 'admin';
}

/** 권한 enum 체크 공통 로직 (admin은 항상 통과) */
function checkPermission(perm: NotifyPermission, role: RoleLike, isAdminOfBoard: boolean): boolean {
  if (isAdminOfBoard) return true;
  if (role === 'owner' || role === 'admin') return true;
  switch (perm) {
    case 'admin-only': return false;
    case 'everyone': return true;
    case 'members':
    default: return role === 'member';
  }
}

/** 게시글에서 @all 사용 가능 여부 — 워크스페이스 board manager 도 admin 동등 처리 */
export function canUseAllMention(
  board: Board,
  role: RoleLike,
  userId?: string,
  workspaceManagerIds?: string[],
): boolean {
  role = effectiveBoardRole(board, role);
  if (isAdminEquivalent(board, role, userId, workspaceManagerIds)) return true;
  return checkPermission(board.notify_all_permission, role, false);
}

// canUseTargetMention 폐기:
// 작성권한이 있으면 @개인 멘션은 당연히 가능. 별도 권한으로 분리할 필요 없음.
// 호출자는 canWritePost / canWriteComment 결과를 그대로 사용하면 됨.

/** 스코프별 허용 유형
 *  채널 게시판은 일반/위키 두 가지만 — 채널 멤버 누구나 작성/읽기 가능하고,
 *  관리자는 admin_ids로 채널 멤버 중에서 별도 지정한다.
 *  이슈 게시판(issue)은 type 자체는 유지하되 UI 옵션에서는 노출 안 함 — workspace 목록에서 제외.
 */
export const ALLOWED_TYPES_BY_SCOPE: Record<BoardScope, BoardType[]> = {
  workspace: ['general', 'notice', 'wiki'],
  channel: ['general', 'wiki'],
  personal: ['general'],
};

/** 유형별 추천 디자인 스타일 (canonical 3 + wiki) */
export const DEFAULT_STYLE_BY_TYPE: Record<BoardType, DisplayStyle> = {
  general: 'notion',
  notice: 'basecamp',
  qna: 'discourse',
  issue: 'basecamp',   // legacy: 'linear' → basecamp 로 fallback
  wiki: 'wiki',
  tasks: 'basecamp',   // legacy: 'linear' → basecamp. Tasks 전용 렌더러는 별도 컴포넌트(TasksInlineView)
};

/** ── Board ── */
export interface Board {
  id: string;
  workspace_id: string;
  channel_id: string | null;
  scope: BoardScope;
  name: string;
  description: string | null;
  type: BoardType;
  owner_id: string;
  admin_ids: string[];                   // owner 외 추가 관리자 UID 목록 (특히 채널 게시판용)
  write_permission: WritePermission;
  comment_permission: CommentPermission;
  /** @all 멘션 발송 권한 — 게시글에서 @all 사용 가능한 역할 */
  notify_all_permission: NotifyPermission;
  min_role: MinRole | null;
  post_count: number;
  last_post_at: string | null;
  default_tags: string[] | null;
  display_style: DisplayStyle | null;
  metadata: Record<string, any> | null;  // 비구조적 설정 (UI 테마 등)
  category_id: string | null;            // 게시판 그룹 — board_categories.id, NULL = 미지정
  category_sort_order: number;           // 같은 카테고리 내 보드 순서
  created_at: string;
  archived_at: string | null;
}

/** ── BoardCategory ── 게시판 그룹화 (워크스페이스 단위) */
export interface BoardCategory {
  id: string;
  workspace_id: string;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/** ── Post ── */
export interface Post {
  id: string;
  board_id: string;
  workspace_id: string;
  title: string;
  content: any | null; // TipTap JSON
  plain_text: string | null;
  author: PostAuthor;
  tags: string[] | null;
  is_pinned: boolean;
  is_announcement: boolean;
  view_count: number;
  like_count: number;
  liked_by: string[];
  reactions: Record<string, string[]>;  // { smile: [uid], heart: [uid, uid2], ... }
  comment_count: number;
  metadata: Record<string, any> | null;
  category_id: string | null;          // (deprecated, wiki_categories FK — 신규 위키는 parent_post_id 사용)
  parent_post_id: string | null;       // 위키 페이지 계층의 부모 페이지 ID (NULL=루트, 위키 게시판 전용)
  tree_sort_order: number;             // 같은 parent_post_id 안 형제 순서 (위키 사이드바 수동 정렬용. 작을수록 위)
  created_at: string;
  updated_at: string | null;
  mentioned_user_ids: string[] | null;  // @멘션된 사용자 ID 배열
  mention_all: boolean;                 // @all 멘션 사용 여부 (워크스페이스 전체 알림)
  attachments: PostAttachment[];        // 일반 파일 첨부 (이미지 외 PDF/DOCX/ZIP 등). 이미지는 본문 TipTap content 안.
}

/** 게시글 첨부파일 — CommentAttachment 와 동일 구조 (jsonb) */
export type PostAttachment = CommentAttachment;

/** 댓글 첨부파일 — 채팅의 FileAttachment 형식과 호환 */
export interface CommentAttachment {
  filename: string;
  url: string;
  sizeByte: number;
  group: 'image' | 'doc';
  /** 출처: 로컬 업로드 / 채팅에서 참조 */
  source?: 'local' | 'chat';
  /** 채팅에서 가져왔을 때 원본 메시지 id (추적용) */
  sourceMessageId?: string;
}

/** ── Comment ── */
export interface Comment {
  id: string;
  target_id: string;
  target_type: 'post' | 'event';
  /** 쓰레드 루트(최상위 댓글 id). null이면 본인이 쓰레드 루트. 같은 쓰레드의 답글들은 모두 동일한 값. */
  parent_comment_id: string | null;
  /** 같은 쓰레드 내에서 어떤 형제 답글에 답한 것인지 (채팅의 replyToMessageId). null이면 쓰레드 전체에 대한 답글. */
  reply_to_comment_id: string | null;
  author: PostAuthor;
  text: string;
  attachments: CommentAttachment[];
  reply_count: number;
  reactions: Record<string, string[]> | null;
  mentioned_user_ids: string[] | null;
  created_at: string;
}

// WikiCategory 인터페이스 폐기 — 위키 페이지 분류는 parent_post_id 계층으로 통합.
// wiki_categories 테이블/post.category_id 컬럼은 데이터 보존 위해 DB 에 남겨두지만 코드에선 사용 안 함.

// ─── Tasks 전용 상수 및 타입 ──────────────────────────────────

/** 태스크 상태 (요청됨 → 진행중 → 검토대기 → 완료) */
export type TaskStatus = 'requested' | 'in_progress' | 'in_review' | 'done';
/** @deprecated InsightStatus → TaskStatus 로 마이그레이션 */
export type InsightStatus = TaskStatus;

/** 태스크 완료 문서 (AI 생성, 원본 태스크 metadata 안에 임베드) */
export interface TaskCompletionDoc {
  summary: string;             // AI 생성 요약
  category?: string | null;    // 분류 (사용자 입력/AI 추천)
  tags?: string[];             // 태그
  key_decisions?: string[];    // 핵심 결정사항
  outcome?: string;            // 최종 결과
  participants?: PostAuthor[]; // 관련 멤버 (작성자/담당자/워쳐)
  period_start: string;        // 태스크 생성일
  period_end: string;          // 태스크 완료일
}

/** 상태 변경 이력 1건 (append-only, metadata.status_history 에 임베드) */
export interface TaskStatusChange {
  from: TaskStatus | null;   // 최초 생성 시 null
  to: TaskStatus;
  by: PostAuthor;            // 변경 주체 (uid/name/photoUrl)
  at: string;               // ISO 8601
  note?: string | null;     // 반려 사유 등 (선택)
}

/** 태스크 메타데이터 (posts.metadata JSONB) */
export interface TaskMetadata {
  status: TaskStatus;
  assignee: PostAuthor | null;
  creator: PostAuthor;                    // 카드 생성자 (기본값·표시·정렬용 — 완료 권한과 무관)
  due_date?: string | null;               // ISO 8601, null = "미정"
  source_type?: 'thread' | 'manual';      // 생성 출처 (thread: 쓰레드→태스크, manual: 직접 생성)
  source_thread_id?: string | null;       // 원본 쓰레드 ID (직접 생성 시 null)
  source_scope?: 'message' | 'thread' | null; // 출처 범위 — message(해당 메시지) / thread(쓰레드 전체)
  source_room_id?: string | null;         // 원본 채팅방 ID (직접 생성 시 null)
  watchers: string[];                     // UID 배열
  completion?: TaskCompletionDoc | null;  // 완료 시 AI가 채우는 완료 문서 (status='done'일 때만 의미 있음)
  status_history?: TaskStatusChange[];    // 상태 변경 이력 (append-only). 없으면 레거시 카드
  last_activity_at: string;               // 마지막 활동 시각 (정렬용)
}
/** @deprecated InsightMetadata → TaskMetadata 로 마이그레이션 */
export type InsightMetadata = TaskMetadata;

/** Records 게시글 메타데이터 (posts.metadata JSONB) */
export interface RecordMetadata {
  source_task_id: string;          // 원본 태스크 ID
  source_thread_id?: string | null; // 원본 쓰레드 ID (직접 생성 태스크는 null)
  source_room_id?: string | null;   // 원본 채팅방 ID
  participants: PostAuthor[];       // 관련 멤버
  period_start: string;             // 태스크 생성일
  period_end: string;               // 태스크 완료일
}
/** @deprecated ArchiveMetadata → RecordMetadata 로 마이그레이션 */
export type ArchiveMetadata = RecordMetadata;

/** 태스크 상태 정의 (색상·순서). 라벨은 getTaskStatusLabel(status) 로 i18n */
export const TASK_STATUS_CONFIG: Record<TaskStatus, {
  color: string;
  bgColor: string;
  order: number;
}> = {
  requested:   { color: 'text-badge-info',     bgColor: 'bg-badge-info-bg',    order: 0 },
  in_progress: { color: 'text-badge-caution',  bgColor: 'bg-badge-caution-bg', order: 1 },
  in_review:   { color: 'text-badge-review',   bgColor: 'bg-badge-review-bg',  order: 2 },
  done:        { color: 'text-badge-success',  bgColor: 'bg-badge-success-bg', order: 3 },
};
/** @deprecated INSIGHT_STATUS_CONFIG → TASK_STATUS_CONFIG */
export const INSIGHT_STATUS_CONFIG = TASK_STATUS_CONFIG;

/**
 * 태스크 상태 라벨 (i18n) — locale 에 따라 동적.
 * config.label 을 정적으로 두면 모듈 로드 시점 locale 로 고정돼 언어 전환이 반영되지 않으므로,
 * 렌더 중 호출하는 함수로 분리.
 */
export function getTaskStatusLabel(status: TaskStatus): string {
  switch (status) {
    case 'requested':   return t`Requested`;
    case 'in_progress': return t`In progress`;
    case 'in_review':   return t`In review`;
    case 'done':        return t`Done`;
    default:            return status;
  }
}

/** 태스크 상태 순서 배열 (UI 셀렉트용) */
export const TASK_STATUS_LIST: TaskStatus[] = ['requested', 'in_progress', 'in_review', 'done'];
/** @deprecated INSIGHT_STATUS_LIST → TASK_STATUS_LIST */
export const INSIGHT_STATUS_LIST = TASK_STATUS_LIST;

/** Post의 metadata에서 TaskMetadata 추출 */
export function getTaskMetadata(post: Post): TaskMetadata | null {
  if (!post.metadata?.status) return null;
  return {
    status: (post.metadata.status as TaskStatus) ?? 'requested',
    assignee: post.metadata.assignee ?? null,
    creator: post.metadata.creator,
    due_date: post.metadata.due_date ?? null,
    source_thread_id: post.metadata.source_thread_id ?? null,
    source_room_id: post.metadata.source_room_id ?? null,
    watchers: post.metadata.watchers ?? [],
    completion: post.metadata.completion ?? null,
    status_history: post.metadata.status_history ?? undefined,
    last_activity_at: post.metadata.last_activity_at ?? post.created_at,
  };
}
/** @deprecated getInsightMetadata → getTaskMetadata */
export const getInsightMetadata = getTaskMetadata;

/**
 * 상태 변경 이력 1건을 metadata.status_history 에 append 한 새 배열을 반환.
 * 서비스 계층에서 상태를 바꿀 때 항상 이 헬퍼로 이력을 남긴다.
 */
export function appendStatusChange(
  currentMetadata: Record<string, any> | null | undefined,
  from: TaskStatus | null,
  to: TaskStatus,
  by: PostAuthor,
  at: string,
  note?: string | null,
): TaskStatusChange[] {
  const prev: TaskStatusChange[] = currentMetadata?.status_history ?? [];
  const entry: TaskStatusChange = { from, to, by, at, ...(note ? { note } : {}) };
  return [...prev, entry];
}

/** Post의 metadata에서 RecordMetadata 추출 */
export function getRecordMetadata(post: Post): RecordMetadata | null {
  const sourceId = post.metadata?.source_task_id ?? post.metadata?.source_insight_id;
  if (!sourceId) return null;
  const meta = post.metadata!;
  return {
    source_task_id: sourceId,
    source_thread_id: meta.source_thread_id ?? null,
    source_room_id: meta.source_room_id ?? null,
    participants: meta.participants ?? [],
    period_start: meta.period_start,
    period_end: meta.period_end,
  };
}
/** @deprecated getArchiveMetadata → getRecordMetadata */
export const getArchiveMetadata = getRecordMetadata;


// ─── Issue 전용 상수 및 타입 ─────────────────────────────────

/** 이슈 상태 */
export type IssueStatus = 'todo' | 'in_progress' | 'done' | 'cancelled';

/** 이슈 우선순위 */
export type IssuePriority = 'none' | 'low' | 'medium' | 'high' | 'urgent';

/** 이슈 메타데이터 (posts.metadata JSONB) */
export interface IssueMetadata {
  status: IssueStatus;
  priority: IssuePriority;
  assignee?: PostAuthor | null;
  due_date?: string | null;   // ISO 8601
}

/** 이슈 상태 정의 (색상·순서). 라벨은 getIssueStatusLabel(s) 로 i18n */
export const ISSUE_STATUS_CONFIG: Record<IssueStatus, {
  color: string;         // Tailwind text color
  bgColor: string;       // Tailwind bg color (뱃지용)
  order: number;
}> = {
  todo:        { color: 'text-blue-500',           bgColor: 'bg-blue-500/10',        order: 0 },
  in_progress: { color: 'text-amber-500',          bgColor: 'bg-amber-500/10',       order: 1 },
  done:        { color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10', order: 2 },
  cancelled:   { color: 'text-muted-foreground/60', bgColor: 'bg-muted/60',          order: 3 },
};

export function getIssueStatusLabel(s: IssueStatus): string {
  switch (s) {
    case 'todo':        return t`To do`;
    case 'in_progress': return t`In progress`;
    case 'done':        return t`Done`;
    case 'cancelled':   return t`Cancelled`;
  }
}

/** 이슈 우선순위 정의 (색상·아이콘·순서). 라벨은 getIssuePriorityLabel(p) 로 i18n */
export const ISSUE_PRIORITY_CONFIG: Record<IssuePriority, {
  color: string;
  icon: string;       // 이모지 또는 텍스트 마커
  order: number;
}> = {
  none:   { color: 'text-muted-foreground/50', icon: '—', order: 0 },
  low:    { color: 'text-blue-400',            icon: '▽', order: 1 },
  medium: { color: 'text-amber-500',           icon: '◆', order: 2 },
  high:   { color: 'text-orange-500',          icon: '▲', order: 3 },
  urgent: { color: 'text-destructive',         icon: '⚡', order: 4 },
};

export function getIssuePriorityLabel(p: IssuePriority): string {
  switch (p) {
    case 'none':   return t`None`;
    case 'low':    return t`Low`;
    case 'medium': return t`Medium`;
    case 'high':   return t`High`;
    case 'urgent': return t`Urgent`;
  }
}

/** 이슈 상태 순서 배열 (UI 셀렉트용) */
export const ISSUE_STATUS_LIST: IssueStatus[] = ['todo', 'in_progress', 'done', 'cancelled'];

/** 이슈 우선순위 순서 배열 */
export const ISSUE_PRIORITY_LIST: IssuePriority[] = ['none', 'low', 'medium', 'high', 'urgent'];

/** Post의 metadata에서 IssueMetadata 추출 */
export function getIssueMetadata(post: Post): IssueMetadata | null {
  if (!post.metadata?.status) return null;
  return {
    status: (post.metadata.status as IssueStatus) ?? 'todo',
    priority: (post.metadata.priority as IssuePriority) ?? 'none',
    assignee: post.metadata.assignee ?? null,
    due_date: post.metadata.due_date ?? null,
  };
}

/** 마감일까지 남은 일수 계산 (음수 = 초과) */
export function getDaysUntilDue(dueDateStr: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/** Supabase Row → Board 변환 */
export function rowToBoard(row: any): Board {
  return {
    id: row.id,
    workspace_id: row.workspace_id,
    channel_id: row.channel_id ?? null,
    scope: row.scope as BoardScope,
    name: row.name,
    description: row.description ?? null,
    type: (row.type ?? 'general') as BoardType,
    owner_id: row.owner_id,
    admin_ids: Array.isArray(row.admin_ids) ? row.admin_ids : [],
    write_permission: (row.write_permission ?? 'members') as WritePermission,
    comment_permission: (row.comment_permission ?? 'everyone') as CommentPermission,
    notify_all_permission: (row.notify_all_permission ?? 'admin-only') as NotifyPermission,
    min_role: (row.min_role ?? null) as MinRole | null,
    post_count: row.post_count ?? 0,
    last_post_at: row.last_post_at ?? null,
    default_tags: row.default_tags ?? null,
    display_style: row.display_style ?? null,
    metadata: row.metadata ?? null,
    category_id: row.category_id ?? null,
    category_sort_order: row.category_sort_order ?? 0,
    created_at: row.created_at ?? new Date().toISOString(),
    archived_at: row.archived_at ?? null,
  };
}

/** Supabase Row → BoardCategory 변환 */
export function rowToBoardCategory(row: any): BoardCategory {
  return {
    id: row.id,
    workspace_id: row.workspace_id,
    name: row.name,
    sort_order: row.sort_order ?? 0,
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? new Date().toISOString(),
  };
}

/** Supabase Row → Post 변환 */
export function rowToPost(row: any): Post {
  return {
    id: row.id,
    board_id: row.board_id,
    workspace_id: row.workspace_id,
    title: row.title,
    content: row.content ?? null,
    plain_text: row.plain_text ?? null,
    author: row.author as PostAuthor,
    tags: row.tags ?? null,
    is_pinned: row.is_pinned ?? false,
    is_announcement: row.is_announcement ?? false,
    view_count: row.view_count ?? 0,
    like_count: row.like_count ?? 0,
    liked_by: row.liked_by ?? [],
    reactions: row.reactions ?? {},
    comment_count: row.comment_count ?? 0,
    metadata: row.metadata ?? null,
    category_id: row.category_id ?? null,
    parent_post_id: row.parent_post_id ?? null,
    tree_sort_order: row.tree_sort_order ?? 0,
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? null,
    mentioned_user_ids: row.mentioned_user_ids ?? null,
    mention_all: row.mention_all === true,
    attachments: Array.isArray(row.attachments) ? (row.attachments as PostAttachment[]) : [],
  };
}

/** Supabase Row → Comment 변환 */
export function rowToComment(row: any): Comment {
  return {
    id: row.id,
    target_id: row.target_id,
    target_type: row.target_type as 'post' | 'event',
    parent_comment_id: row.parent_comment_id ?? null,
    reply_to_comment_id: row.reply_to_comment_id ?? null,
    author: row.author as PostAuthor,
    text: row.text,
    attachments: Array.isArray(row.attachments) ? (row.attachments as CommentAttachment[]) : [],
    reply_count: row.reply_count ?? 0,
    reactions: row.reactions ?? null,
    mentioned_user_ids: row.mentioned_user_ids ?? null,
    created_at: row.created_at ?? new Date().toISOString(),
  };
}
